<script setup>
import Modal from '@/mini/components/Modal.vue'
import Boxes from '@/mini/components/Boxes.vue'
import Box from '@/mini/components/Box.vue'
import Button from '@/mini/components/Button.vue'
import Sep from '@/mini/components/Sep.vue'
import { BrightStar, ListSelect, Bell } from '@iconoir/vue'
import { ref, watch } from 'vue'
import { useMessage } from '@/mini/composables/useMessage'

import { useAuthStore } from '@/mini/stores/auth'
import { BACKEND_ORIGIN } from '@/config/api'

const { showSuccessMessage, showWarningMessage, showDangerMessage } = useMessage()
const authStore = useAuthStore()

const props = defineProps({
  visible: { type: Boolean, default: false },
  card: { type: Object, default: null },
})

const emit = defineEmits(['close', 'edit', 'save-ct-price'])

const isLoadingCtPrice = ref(false)

async function fetchLowestCtPrice() {
  if (!props.card?.cardtrader_blueprint || isLoadingCtPrice.value) return
  const prefix = props.card.card_name ? `${props.card.card_name}: ` : ''
  isLoadingCtPrice.value = true
  try {
    const res = await authStore.authFetch(`${BACKEND_ORIGIN}/api/v1/ct/marketplace/?blueprint_id=${props.card.cardtrader_blueprint}`)
    if (!res.ok) throw new Error(`${res.status}`)
    const data = await res.json()
    const products = Object.values(data).flat()
    const filtered = products.filter(p =>
      p.properties_hash?.condition === 'Near Mint' &&
      p.properties_hash?.onepiece_language === 'jp' &&
      p.user?.can_sell_sealed_with_ct_zero === true
    )
    if (filtered.length) {
      const price = Math.min(...filtered.map(p => p.price_cents)) / 100
      emit('save-ct-price', props.card, price)
      showSuccessMessage(`${prefix}CT price: ${price.toFixed(2)}€`)
    } else {
      showWarningMessage(`${prefix}No 🇯🇵/NM listings found`)
    }
  } catch {
    showDangerMessage(`${prefix}CT price fetch failed`)
  } finally {
    isLoadingCtPrice.value = false
  }
}

watch(() => props.visible, (visible) => {
  if (visible) fetchLowestCtPrice()
})

function fmt(val) {
  if (val == null || val === '') return '—'
  return val
}

function fmtPrice(val) {
  if (val == null || val === '') return '—'
  return Number(val).toFixed(2) + '€'
}

function rarityClass(rarity) {
  switch (rarity) {
    case 'C': return 'false-white-bg grey-text'
    case 'UC': return 'light-grey-bg dark-grey-text'
    case 'R': return 'rare-bg'
    case 'SR': return 'super-rare-bg'
    case 'SEC': return 'sec-bg'
    case 'PR': return 'promo-bg'
    case 'L': return 'leader-bg'
    default: return ''
  }
}

const CARD_COLOR_META = {
  Red: { className: 'red-bg', background: '#e05e5e', text: '#ffffff' },
  Green: { className: 'green-bg', background: '#69b687', text: '#ffffff' },
  Blue: { className: 'blue-card-bg', background: '#3e84bc', text: '#ffffff' },
  Purple: { className: 'purple-bg', background: '#ae5ab9', text: '#ffffff' },
  Black: { className: 'black-card-bg', background: '#58585a', text: '#ffffff' },
  Yellow: { className: 'yellow-bg', background: '#f3dc61', text: '#141428' },
}

function parseCardColors(value) {
  return String(value || '')
    .split(/\s*[/,&+|-]\s*|\s+/)
    .map(part => part.trim())
    .filter(Boolean)
    .filter((part, index, arr) => arr.indexOf(part) === index)
    .filter(part => CARD_COLOR_META[part])
}

function cardColorClass(value) {
  const colors = parseCardColors(value)
  if (colors.length !== 1) return ''
  return CARD_COLOR_META[colors[0]].className
}

function cardColorStyle(value) {
  const colors = parseCardColors(value)
  if (colors.length <= 1) return null

  const step = 100 / (colors.length - 1)
  const gradientStops = colors
    .map((colorName, index) => `${CARD_COLOR_META[colorName].background} ${Math.round(index * step)}%`)
    .join(', ')

  const shouldUseWhiteText = colors.some(colorName => CARD_COLOR_META[colorName].text === '#ffffff')

  return {
    background: `linear-gradient(90deg, ${gradientStops})`,
    color: shouldUseWhiteText ? '#ffffff' : '#141428',
  }
}

</script>

<template>
  <Modal :visible="visible" @close="emit('close')">
    <Boxes v-if="card">
      <Box size="100" padding="0">
        <Boxes gap="0">
          <Box v-if="card.card_image" size="50">
            <img :src="card.card_image" :alt="card.card_name" class="img" style="width: 100%; height: auto;" />
          </Box>
          <Box :size="card.card_image ? 50 : 100">
            <h2 class="m-0">{{ card.card_name }}</h2>
            <p class="mt-0 label">{{ card.card_id }}</p>
            <p class="">
              <span
                class="py-05 px-1 b-rad-5 me-05"
                :class="cardColorClass(card.card_color)"
                :style="cardColorStyle(card.card_color)"
              >
                {{ card.card_color }}
              </span>
              <span v-if="card.favourite" class="py-05 px-1 b-rad-5 me-05 white-text" style="background-color: gold;">
                <BrightStar width="18" height="18" style="vertical-align: text-bottom;"/>&nbsp;
                Favourite
              </span>
              <span v-if="card.to_buy" class="py-05 px-1 b-rad-5 me-05 warning-bg">
                <ListSelect width="16" height="16" style="vertical-align: text-bottom;"/>&nbsp;
                Wishlist
              </span>
              <span v-if="card.to_follow" class="py-05 px-1 b-rad-5 me-05 white-text" style="background-color: #0ea5e9;">
                <Bell width="16" height="16" style="vertical-align: text-bottom;"/>&nbsp;
                Following
              </span>
              <span v-if="card.cardtrader_product_id" class="blue-bg py-05 px-1 b-rad-5 me-05 white-text">To sell</span>
              <span v-else-if="card.sell_to_sell" class="success-bg py-05 px-1 b-rad-5 me-05 white-text">To sell</span>
            </p>
            <p class="">
              <span v-if="card.rarity" class="p-05" :class="rarityClass(card.rarity)">{{ card.rarity }}</span>
              <span v-if="card.foil === true || card.foil === 'TRUE'" class="gold-bg p-05">Foil</span>
            </p>
            <p class="S">
              <span v-if="card.card_type" class="fw-bg py-05 px-1">{{ card.card_type }}</span>
            </p>
          </Box>
        </Boxes>
      </Box>

      <Sep />

      <Box size="100" class="p-0">
        <Boxes gap="0">
          <Box size="100">
            <h3 class="p m-0">Prices</h3>
          </Box>
          <Box class="box-zero-33 box-sm-16">
            <p class="S label mb-0">Inventory</p>
            <p class="m-0"><b>{{ fmtPrice(card.inventory_price) }}</b></p>
          </Box>
          <Box class="box-zero-33 box-sm-16">
            <p class="S label mb-0">Market</p>
            <p class="m-0"><b>{{ fmtPrice(card.market_price) }}</b></p>
          </Box>
          <Box class="box-zero-33 box-sm-16">
            <p class="S label mb-0">Real value</p>
            <p class="m-0"><b>{{ fmtPrice(card.real_price) }}</b></p>
          </Box>
          <Box class="box-zero-33 box-sm-16">
            <p class="S label mb-0 blue-text">CT (min)</p>
            <p class="m-0 blue-text"><b>{{ fmtPrice(card.cardtrader_market_price) }}</b></p>
          </Box>
          <Box class="box-zero-33 box-sm-16">
            <p class="S label mb-0 blue-text">CT lowest</p>
            <p class="m-0 blue-text">{{ fmtPrice(card.cardtrader_lowest_market_price) }}</p>
          </Box>
          <Box class="box-zero-33 box-sm-16">
            <p class="S label mb-0">Purchased at</p>
            <p class="m-0"><b>{{ fmtPrice(card.purchase_price) }}</b></p>
          </Box>
        </Boxes>
      </Box>
      <Sep />
      <Box size="100" class="p-0">
        <Box size="100">
          <h3 class="p m-0 blue-text">CardTrader</h3>
        </Box>
        <Boxes gap="0">
          <Box class="box-zero-33">
            <p class="S label mb-0">Blueprint</p>
            <p class="m-0 blue-text">{{ fmt(card.cardtrader_blueprint) }}</p>
          </Box>
          <Box class="box-zero-33">
            <p class="S label mb-0">Expansion</p>
            <p class="m-0 blue-text">{{ fmt(card.cardtrader_expansion) }}</p>
          </Box>
          <Sep/>
          <Box class="box-zero-33">
            <p class="S label mb-0">Listing</p>
            <p class="m-0">
              <span v-if="card.cardtrader_product_id" class="bold blue-text">On sale</span>
              <span v-else class="">—</span>
            </p>
          </Box>
          <Box v-if="card.cardtrader_product_id" class="box-zero-33">
              <p class="S label mb-0">Sell price</p>
              <p class="m-0 blue-text"><b>{{ fmtPrice(card.cardtrader_sell_price) }}</b></p>
          </Box>
          <Box v-if="card.cardtrader_product_id" class="box-zero-33">
              <p class="S label mb-0">Sell qty</p>
              <p class="m-0"><b>{{ fmt(card.cardtrader_sell_quantity) }}</b></p>
          </Box>
          <Box v-if="card.cardtrader_blueprint" size="100" class="">
            <Button size="S" color="blue" class="px-15 round" :link="`https://www.cardtrader.com/en/cards/${card.cardtrader_blueprint}`" target="_blank">
              View on CT ↗
            </Button>
          </Box>
        </Boxes>
      </Box>

      <template v-if="card.group_entries?.length && !card.to_buy">
        <Sep />
        <Box size="100" class="py-0">
          <h3 class="p m-0 bold">Storage</h3>
        </Box>
        <Box size="100" padding="0">
          <Boxes gap="0">
            <Box
              v-for="(entry, i) in card.group_entries"
              :key="i"
              size="100"
              padding="0"
              class="box-shadow p-15 b-rad-10 mb-1"
            >
              <Boxes gap="0">
                <Box class="box-zero-33 box-sm-20">
                  <p class="S label mb-0">Group</p>
                  <p class="m-0"><b>{{ fmt(entry.group_name) }}</b></p>
                </Box>
                <Box class="box-zero-33 box-sm-20">
                  <p class="S label mb-0">Qty</p>
                  <p class="m-0"><b>{{ entry.quantity }}</b></p>
                </Box>
                <Box v-if="!entry.warehouse" class="box-zero-33 box-sm-20">
                  <p class="S label mb-0">Folder</p>
                  <p class="m-0"><b>{{ fmt(entry.storage_folder) }}</b></p>
                </Box>
                <Box v-if="!entry.warehouse" class="box-zero-33 box-sm-20">
                  <p class="S label mb-0">Page</p>
                  <p class="m-0">{{ fmt(entry.storage_page) }}</p>
                </Box>
                <Box v-if="entry.warehouse" class="box-zero-33 box-sm-20">
                  <p class="S label mb-0">Location</p>
                  <p class="m-0 blue-text"><b>CT Warehouse</b></p>
                </Box>
              </Boxes>
            </Box>
          </Boxes>
        </Box>
      </template>

      <Sep />

      <Box size="100">
        <Button color="light-grey" class="round px-15" @click="emit('close')">Close</Button>
        <Button color="warning" class="round px-15" @click="emit('edit', card)">Edit</Button>
      </Box>
    </Boxes>
  </Modal>
</template>

<style scoped lang="scss">
.red-bg {
    background: #e05e5e;
    color: #ffffff;
}
.green-bg {
    background: #69b687;
    color: #ffffff;
}
.blue-card-bg {
    background: #3e84bc;
    color: #ffffff;
}
.purple-bg {
    background: #ae5ab9;
    color: #ffffff;
}
.black-card-bg {
    background: #58585a;
    color: #ffffff;
}
.yellow-bg {
    background: #f3dc61;
    color: #141428;
}
.gold-bg {
    background: #ffec19;
    background: linear-gradient(45deg,rgba(255, 236, 25, 1) 0%, rgba(207, 181, 59, 1) 100%);
    /*border: 1px solid #CFB53B;*/
    color: #ffffff;
    font-weight: bold;
}
.rare-bg {
  background: #ffffff;
  background: linear-gradient(45deg,rgba(255, 255, 255, 1) 0%, rgba(179, 179, 179, 1) 100%);
  /*border: 1px solid var(--light-grey);*/
}
.leader-bg {
  background: rgb(196 64 137);
  background: linear-gradient(45deg, rgb(103 24 247) 0%, rgb(196 64 137) 100%);
  /*border: 1px solid var(--false-black);*/
  color: var(--white);
}
.super-rare-bg {
  background: #ffcb47;
  background: linear-gradient(45deg,rgba(255, 203, 71, 1) 0%, rgba(255, 125, 125, 1) 100%);
  /*border: 1px solid #FF7D7D;*/
  color: #FFFFFF;
  font-weight: bold;
}
.promo-bg {
  background: #61c2ff;
  background: linear-gradient(45deg,rgba(97, 194, 255, 1) 0%, rgba(137, 104, 227, 1) 100%);
  /*border: 1px solid #8968E3;*/
  color: #FFFFFF;
  font-weight: bold;
}
.sec-bg {
  background: #ff9900;
  background: linear-gradient(45deg,rgba(255, 153, 0, 1) 1%, rgba(209, 25, 25, 1) 100%);
  /*border: 1px solid #D11919;*/
  color: var(--white);
}
</style>